import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { NormalizationSql, QuestionResponse } from './Models';
import Highlighted from "./Highlited";
import DeleteButton from "./DeleteButton";

import Button from '@mui/material/Button';
import { Box, Modal, Stack, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import QRForm from './components/QRForm';
import { HttpService } from './services/HttpService';
import { useHttp } from './hooks/useHttp';


const codeBlock = `[SCHEMA]
BEGIN;


CREATE TABLE IF NOT EXISTS public."Accounts"
(
    "AccountId" integer NOT NULL GENERATED BY DEFAULT AS IDENTITY ( INCREMENT 1 START 1 MINVALUE 1 MAXVALUE 2147483647 CACHE 1 ),
    "AccountNumber" character varying(20) COLLATE pg_catalog."default" NOT NULL,
    "Balance" numeric(18, 2) NOT NULL,
    "AccountType" integer NOT NULL,
    "CreatedDate" timestamp with time zone NOT NULL,
    CONSTRAINT "PK_Accounts" PRIMARY KEY ("AccountId")
);


END;
`

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: "75%",
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const defaultModel: NormalizationSql = {
  id: null, name: null, sql: codeBlock, normalized: null, simplified: null, questionsResponses: [], exampleOrder: null,
};

const apiHosts = ["http://localhost:5265/", "https://sql-normalizer.marat.app/"]

function App() {

  const [isOpen, setIsOpen] = useState(false);
  const [isNormalizedEditorOpen, setIsNormalizedEditorOpen] = useState(false);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newQR, setNewQR] = useState<QuestionResponse | null>(null);
  const [currentQR, setCurrentQR] = useState<QuestionResponse | null>(null);

  const [apiHost, setApiHost] = useState(process.env.REACT_APP_SQL_NORMALIZER_BACKEND + "/" || "https://sql-normalizer.marat.app/");
  const [httpService, setHttpSerivce] = useState(new HttpService(apiHost));

  const [model, setModel] = useState<NormalizationSql>(defaultModel);
  const [models, setModels] = useState<NormalizationSql[]>([]);

  const [question, setQuestion] = useState<string>("");
  const [generatedSql, setGeneratedSql] = useState<string>("");

  const {
      data: invalidDenormalized,
      loading: validatingDenormalization,
      error: validatingDenormalizationError,
      sendRequest: sendValidatingDenormalizationRequest
  } = useHttp(() => httpService.post<[]>('/api/normalization/validateResponses', {
      sql: model.sql,
      Response: model.questionsResponses.map(r => {
        return {
          id: r.id,
          response: r.response,
          responseOrig: r.responseOrig,
        }}),
  }));

  useEffect(() => {
      setHttpSerivce(new HttpService(apiHost));
  }, [setHttpSerivce, apiHost]);

  const validateResponses = useCallback(async () => {
      var invalid = await sendValidatingDenormalizationRequest();
      if (invalid && invalid.length > 0) {
          alert("Invalid responses found.");
          console.log(invalid);
      } else {
        alert("All responses are valid.");
      }
  }, [model.questionsResponses, sendValidatingDenormalizationRequest]);

  const modelsFetchedRef = useRef(false);
  if (!modelsFetchedRef.current) {
    setIsLoading(true);
    fetch(`${apiHost}api/normalization`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        setModels(data.value);
        if (data.value.length > 0) {
          if (model && model.id) {
            for (const m of data.value) {
              if (model.id == m.id) {
                setModel(m);
              }
            }
          }
          else {
            setModel(data.value[0]);
          }
        }
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
    modelsFetchedRef.current = true;
  }

  const onHostChange = useCallback((event: SelectChangeEvent) => {
    setApiHost(event.target.value as string);
    modelsFetchedRef.current = false;
  }, [setApiHost]);

  const onNormalize = useCallback(() => {
    setIsLoading(true);
    fetch(`${apiHost}api/normalization/normalize`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sql: model.sql
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        setModel({ ...model, normalized: data.value.normalized, simplified: data.value.simplified });
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
  }, [apiHost, model, model.sql, setIsLoading]);

  const onNormalizeQRs = useCallback(() => {
    setIsLoading(true);
    fetch(`${apiHost}api/normalization/normalizeQRs`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sql: model.sql,
        questionsResponses: model.questionsResponses,
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        debugger;
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        setModel({ ...model, questionsResponses: data.value });
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
  }, [apiHost, model, model.questionsResponses, setIsLoading]);

  const onGenerateSql = useCallback(() => {
    setIsLoading(true);
    fetch(`${apiHost}api/SqlGenerator`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        Schema: model.sql,
        SchemaNormalized: model.normalized,
        Question: question,
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        setGeneratedSql(data.value);
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
  }, [apiHost, model, model.sql, setIsLoading, setGeneratedSql, generatedSql, question]);


  const onExport = useCallback(() => {
    setIsLoading(true);
    fetch(`${apiHost}api/normalization/export/${model.id}`, {
      method: 'GET',
      headers: {
        'Content-Type': "application/octet-stream"
      }
    }).then(async (response) => {
      const blob = await response.blob();
      let filename = "Export.txt";
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      if (contentDispositionHeader !== null) {
        const re = /filename="(.+?)";/i;
        const match = contentDispositionHeader.match(re);
        if (match !== null) {
          filename = match[1];
        }
      }
      // Create blob link to download
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        filename,
      );

      // Append to html link element page
      document.body.appendChild(link);

      // Start download
      link.click();

      // Clean up and remove the link
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
      setIsLoading(false);
    });
  }, [apiHost, model, setIsLoading]);

  const onAddQuestionsResponses = useCallback((newQR: QuestionResponse) => {
    setModel({ ...model, questionsResponses: [...model.questionsResponses, newQR] });
    setNewQR(null);
  }, [model, newQR, setNewQR]);

  const onSave = useCallback(() => {
    if (model.normalized === null || model.normalized.trim() === "") {
      alert("Normalized sql is required.")
      return;
    }
    if (model.name === null || model.name.trim() === "") {
      alert("Name is required");
      return;
    }
    setIsLoading(true);
    const action = model.id !== "" && model.id !== null ? "update" : "create";
    fetch(`${apiHost}api/normalization/${action}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(model)
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        if ("status" in data && data.status !== 200) {
          alert(data.errors[0])
          return;
        }
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        if (data.value) {
          setModel(data.value);
          modelsFetchedRef.current = false;
        }
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
  }, [apiHost, model, setIsLoading]);

  const onDel = useCallback(() => {
    if (!model.id) {
      alert("Item is not selected.");
      return;
    }
    setIsLoading(true);
    fetch(`${apiHost}api/normalization/${model.id}`, {
      method: 'DELETE',
    })
      .then(response => response.json())
      .then(data => {
        if (data.status >= 400) {
          alert(data.errors['$']);
          return;
        }
        if (data.isError) {
          alert(data.firstError.description);
          return;
        }
        setModel(defaultModel);
        modelsFetchedRef.current = false;
      })
      .catch(error => alert(error))
      .finally(() => setIsLoading(false));
  }, [apiHost, model, setIsLoading]);

  const onNew = useCallback(() => {
    setModel({ ...model, id: null, name: "", questionsResponses: [], exampleOrder: null });
  }, [model, setModel]);

  const onNameChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setModel({ ...model, name: event.target.value });
  }, [model, model.name, setModel]);

  const onExampleOrderChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setModel({ ...model, exampleOrder: event.target.value ? Number(event.target.value) : null });
  }, [model, model.exampleOrder, setModel]);

  const onQuestionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value);
  }, [setQuestion]);

  const getQRHandler = (qr: QuestionResponse) => () => {
    setCurrentQR(qr);
    setIsResponseOpen(true);
  };

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      <Select
        labelId="host-select-label"
        id="host-select"
        value={apiHost}
        label="Normalizer API host"
        onChange={onHostChange}
        variant='filled'
      >
        <MenuItem value={apiHosts[0]}>{apiHosts[0]}</MenuItem>
        <MenuItem value={apiHosts[1]}>{apiHosts[1]}</MenuItem>
      </Select>
      <Divider orientation="vertical" flexItem> </Divider>
      <Stack direction="row" spacing={2}>
        <Autocomplete
          options={models}
          getOptionLabel={(option: NormalizationSql) => (option.name || "") + (option.exampleOrder !== null ? (" - " + option.exampleOrder?.toString()) : "")}
          sx={{ width: 300 }}
          renderInput={(params) => <TextField {...params} label="Normalized SQL" />}
          value={model}
          onChange={(event: any, newValue: NormalizationSql | null) => {
            if (newValue !== null) {
              setModel(newValue);
            }
          }}
        />
        <TextField variant="filled" sx={{ width: 400 }} label="Name" value={model.name} onChange={(onNameChange)} />
        <TextField variant="filled" sx={{ width: 30 }} label="Example Order" value={model.exampleOrder || ""} onChange={(onExampleOrderChange)} />
        <DeleteButton id={"del_id"} onConfirmed={onDel} disabled={model?.id === null} />
        <Button variant="contained" color='warning' onClick={onNew}>new</Button>
        <Button variant="contained" color='secondary' onClick={onExport}>export</Button>
      </Stack>

      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid size={7} padding={1} >
          <Highlighted code={model.sql || ""} onDoubleClick={() => setIsOpen(true)} />
        </Grid>
        <Grid size={5} paddingTop={1}>
          <Highlighted code={model.normalized || ""} onDoubleClick={() => setIsNormalizedEditorOpen(true)} />
          <Highlighted code={model.simplified || ""} onDoubleClick={() => { }} />
          <TextField variant="filled" style={{ width: "100%" }} label="Question" value={question} onChange={onQuestionChange} />
          <TextField variant="filled" style={{ width: "100%" }} label="Generated SQL" value={generatedSql} />
          <Button variant="contained" fullWidth onClick={onGenerateSql}>Generate SQL</Button>
        </Grid>
        <Grid size={2}>
          <Button variant="contained" fullWidth onClick={onNormalize}>normalize schema</Button>
        </Grid>
        <Grid size={1.5}>
          <Button variant="contained" fullWidth onClick={onNormalizeQRs}>normalize QR</Button>
        </Grid>
        <Grid size={1.5}>
          <Button variant="contained" fullWidth onClick={() => { setIsOpen(!isOpen) }}>edit</Button>
        </Grid>
        <Grid size={2}>
          <Button variant="contained" fullWidth onClick={validateResponses}>validate</Button>
        </Grid>
        <Grid size={2.5}>
          <Button variant="contained" fullWidth onClick={() => { setIsAddOpen(!isAddOpen); }}>Add QR</Button>
        </Grid>
        <Grid size={2.5}>
          <Button variant="contained" color='secondary' fullWidth onClick={onSave}>save</Button>
        </Grid>
        <Grid size={12}>
          {model.questionsResponses.map((qr, idx) => {
            return <Accordion style={{ border: "3px", backgroundColor: qr.id ? "white" : "yellowgreen" }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1-content"
                id="panel1-header"
              >
                <Typography component="span">{idx + 1}. {qr.questionOrig ? qr.questionOrig : qr.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography component="span">{qr.question}</Typography>
              </AccordionDetails>
              <AccordionDetails>
                <Highlighted code={qr.responseOrig} onDoubleClick={getQRHandler(qr)} />
              </AccordionDetails>
              <AccordionDetails>
                <Highlighted code={qr.response} onDoubleClick={getQRHandler(qr)} />
              </AccordionDetails>
              <DeleteButton id={idx.toString()} onConfirmed={() => {
                const itemIdx = model.questionsResponses.indexOf(qr);
                if (itemIdx < 0) {
                  console.log(`QR not found ${qr}`)
                  return;
                }
                model.questionsResponses.splice(itemIdx, 1);
                setModel({ ...model });
              }} disabled={model?.id === null} />
              <Button variant="contained" onClick={getQRHandler(qr)}>edit</Button>
            </Accordion>
          })}
        </Grid>
      </Grid>

      <Modal
        open={isOpen}
        onClose={() => { setIsOpen(false); }}
        aria-labelledby="modal-sql"
        aria-describedby="modal-sql-description"
      >
        <Box sx={modalStyle}>
          <TextField
            multiline
            maxRows={25}
            value={model.sql}
            style={{ width: "100%" }}
            onChange={useCallback((v: React.ChangeEvent<HTMLInputElement>) => {
              setModel({ ...model, sql: v.target.value, normalized: "" });
            }, [model, setModel])} />
        </Box>
      </Modal>

      <Modal
        open={isNormalizedEditorOpen}
        onClose={() => { setIsNormalizedEditorOpen(false); }}
        aria-labelledby="modal-normalized-title"
        aria-describedby="modal-normalized-description"
      >
        <Box sx={modalStyle}>
          <TextField multiline value={model.normalized} style={{ width: "100%" }} onChange={useCallback((v: React.ChangeEvent<HTMLInputElement>) => {
            setModel({ ...model, normalized: v.target.value });
          }, [model, setModel])} />
        </Box>
      </Modal>

      <Modal
        open={isResponseOpen}
        onClose={() => setIsResponseOpen(false)}
        aria-labelledby="modal-response-title"
        aria-describedby="modal-response-description"
      >
        <QRForm qr={currentQR} sql={model.sql} apiHost={apiHost} modalStyle={modalStyle} onChangeCallback={(qr: QuestionResponse) => {
          setModel({ ...model });
        }} />
      </Modal>

      <Modal
        open={isAddOpen}
        onClose={() => { setIsAddOpen(false); }}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <QRForm qr={newQR} sql={model.sql} apiHost={apiHost} modalStyle={modalStyle} onChangeCallback={(qr: QuestionResponse) => {
          setNewQR(new QuestionResponse(qr.id, qr.question, qr.questionOrig, qr.response, qr.responseOrig));
        }} onAddQuestionsResponsesCallback={onAddQuestionsResponses} />
      </Modal>

      <Backdrop
        sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })}
        open={isLoading}
        onClick={() => setIsLoading(!isLoading)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
}

export default App;
